import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  HeaderUserData,
  SedsLoadersCoreModeOptions,
} from 'experian-design-system';
import { EnvironmentService } from 'experian-shared-frontend';
import { Subject, takeUntil } from 'rxjs';
import { LinksConfig } from 'src/app/links.config';
import { DataLayer, PageInfo } from 'src/app/models/analytics.model';
import {
  MyTermAcceptRequest,
  MyTermResponse,
  TermAcceptRequest,
} from 'src/app/models/terms.model';
import { DriveService } from 'src/app/services/drive.service';
import { TermsService } from 'src/app/services/terms.service';

type TMyTerm = MyTermResponse & { checked: boolean };

interface MenuButtonEvent {
  id: number;
  buttonLabel?: string;
  buttonIconLabel?: string;
  iconCustomClass?: string;
  callbackButtonItemClick?: boolean;
}

@Component({
  selector: 'app-terms',
  templateUrl: './terms.component.html',
  styleUrls: ['./terms.component.scss'],
  standalone: false,
})
export class TermsComponent implements OnInit, OnDestroy {
  public userFullName = '';
  public userData: HeaderUserData;
  public myTerms: TMyTerm[] = [];
  public showRequiredTerm = false;
  public success = false;
  public successSubtitle = '';
  public applicationLinks = new LinksConfig();
  public _attrs = {
    width: '243',
  };
  public _attrsSuccess = {
    width: '80',
    height: '80',
  };

  public modeIndeterminate = SedsLoadersCoreModeOptions.INDETERMINATE;

  public showAlertError = false;
  public hasError = false;
  public alertErrorTitle: string;
  public alertErrorMessage: string;
  public isNotMandatory = false;

  private readonly KEY_LOCAL_STORGE_CURR_USER = 'fullNameCurrentUser';
  private readonly destroy$ = new Subject<void>();
  private readonly _driveService = inject(DriveService);
  private readonly _termsService = inject(TermsService);
  private readonly _router = inject(Router);
  private readonly _environmentService = inject(EnvironmentService);

  ngOnInit() {
    this.userFullName = localStorage.getItem(this.KEY_LOCAL_STORGE_CURR_USER);

    this.userData = {
      name: this.userFullName,
      showAvatar: false,
      menuButtonList: [
        [
          {
            id: 1,
            buttonLabel: 'Sair',
            buttonIconLabel: 'logout',
            iconCustomClass: 'logout',
            callbackButtonItemClick: true,
          },
        ],
      ],
    };

    this.sendAnalyticsFormsPageview();
    this._removeItemsFromSubMenu();
    this._initObservables();
  }

  public onButtonItemClick(itemMenu: MenuButtonEvent): void {
    this._router.navigate(['/login']);
  }

  private sendAnalyticsFormsPageview(): void {
    this.buildDataLayer();

    if (document.DataLayer) {
      document.DataLayer.pageInfo.pageName =
        'MP:LG:IN:MenuDeProdutos:TermosDeUsoPrivacidade';
      document.DataLayer.pageInfo.siteSection = 'LG';

      this.dispatchEvent('CustomPageView');
    }
  }

  private sendAnalyticsFormsPageviewSuccess(): void {
    this.buildDataLayer();

    if (document.DataLayer) {
      document.DataLayer.pageInfo.pageName =
        'MP:LG:IN:MenuDeProdutos:TermosDeUsoPrivacidade:Sucesso';
      document.DataLayer.pageInfo.siteSection = 'LG';

      this.dispatchEvent('CustomPageView');
    }
  }

  private sendAnalyticsFormsPageviewListError(): void {
    this.buildDataLayer();

    if (document.DataLayer) {
      document.DataLayer.pageInfo.pageName =
        'MP:LG:IN:MenuDeProdutos:TermosDeUsoPrivacidade:ErroAoExibirTermo';
      document.DataLayer.pageInfo.siteSection = 'LG';

      this.dispatchEvent('CustomPageView');
    }
  }

  private sendAnalyticsFormsEventErrorMandatoryTerms(): void {
    this.sendAnalyticsFormsPageview();
    const error = new HttpErrorResponse({
      status: 403,
      url: '/digital/terms/v1/my-terms/accept-terms',
    });

    const tagObj: any = {
      events: ['error'],
      errorMessage: `ErroTermoObrigatorioNaoAceito:Termo obrigatório:error`,
    };

    if (error) {
      tagObj.errorDetail = this.getErrorDetail(error, 'POST');
    }

    setTimeout(() => {
      document.DataLayer.custom = document.DataLayer?.custom
        ? { ...document.DataLayer.custom, ...tagObj }
        : tagObj;
      this.dispatchEvent('CustomError');
    }, 1000);
  }

  acceptTerms(): void {
    this.showRequiredTerm = false;
    const hasRequiredTermWithoutCheck = this.myTerms.filter(
      (term) => !term.checked && term.termVersion.acceptanceRequired
    );
    if (hasRequiredTermWithoutCheck?.length) {
      this.alertErrorTitle = 'Alguns termos obrigatórios não foram aceitos';
      this.alertErrorMessage =
        'É necessário aceitar os termos obrigatórios para prosseguir.';
      this.showRequiredTerm = true;
      this.sendAnalyticsFormsEventErrorMandatoryTerms();
      return;
    }

    this._handleAcceptTerms();
  }

  getTermsContent(termId: string): void {
    const accessToken = localStorage.getItem('accessToken');
    this.showAlertError = false;

    this._termsService
      .retrieveMyTerm(termId, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (termsResponse) => {
          if (termsResponse.content) {
            this._driveService
              .generateFileUrl([
                {
                  fileId: termsResponse.content,
                  typeURL: 'TEMP',
                },
              ])
              .subscribe({
                next: (driveResponse) => {
                  window.open(driveResponse[0].fileURL, '_blank');
                },
                error: (error: HttpErrorResponse) => {
                  this.sendAnalyticsFormsPageviewListError();
                  this._handleLinkError();
                },
              });
          }
        },
        error: (error: HttpErrorResponse) => {
          this.sendAnalyticsFormsPageviewListError();
          this._handleLinkError();
        },
      });
  }

  setCheckedTerm(termId: string, event: Event): void {
    this.showRequiredTerm = false;
    this.showAlertError = false;

    const checked = (event?.target as HTMLInputElement)?.checked;
    this.myTerms.forEach((term) => {
      if (term.termId === termId) {
        term.checked = checked;
      }

      return term;
    });
  }

  private _verifyTerms(terms: MyTermResponse[]): void {
    if (terms?.length) {
      terms.forEach((curr) => {
        this.myTerms.push({
          ...curr,
          checked: false,
        });
      });
    } else {
      this._goToHome();
    }
  }

  ngOnDestroy() {
    localStorage.removeItem(this.KEY_LOCAL_STORGE_CURR_USER);

    this.destroy$.next();
    this.destroy$.complete();
  }

  private _goToHome(): void {
    this._router.navigate(['']);
  }

  private _removeItemsFromSubMenu(): void {
    setTimeout(() => {
      const listItemHtml: HTMLElement = document.querySelector('.list-item');
      listItemHtml.style.opacity = '0';

      const item0 = document.querySelector('.list-item .item#item-0');
      const item1 = document.querySelector('.list-item .item#item-1');
      const item2 = document.querySelector('.list-item .item#item-2');

      item0?.remove();
      item1?.remove();
      item2?.remove();
      listItemHtml.style.opacity = '1';
    }, 100);
  }

  private _handleAcceptTerms(): void {
    const arrTerms = this._createArrayOfTerms();
    const accessToken = localStorage.getItem('accessToken');
    const body: MyTermAcceptRequest = {
      terms: [...arrTerms],
    };

    this.successSubtitle = this.myTerms
      .filter((term) => term.checked)
      .reduce((acc, curr) => {
        return acc + ` ${curr.summary},`;
      }, '');

    this._termsService
      .acceptMyTerms(body, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.success = true;
          this.sendAnalyticsFormsPageviewSuccess();
          setTimeout(() => {
            this._goToHome();
          }, 3000);
        },
        error: (error: HttpErrorResponse) => {
          this.alertErrorTitle =
            'Não foi possível prosseguir com o aceite dos termos de uso.';
          this.alertErrorMessage =
            'Por favor, tente novamente mais tarde e, se o erro persistir, entre em contato com a nossa central de atendimento.';
          this.showAlertError = true;
        },
      });
  }

  private _createArrayOfTerms(): TermAcceptRequest[] {
    const terms = [];

    for (const term of this.myTerms) {
      if (term.checked) {
        terms.push({
          termId: term.termId,
          action: 'ACCEPTED',
        });
      }
    }

    return terms;
  }

  private _initObservables(): void {
    this._termsService.termIsMandatoryAcceptance$.subscribe(
      (res) => (this.isNotMandatory = res)
    );

    this._termsService.termsData$
      .pipe(takeUntil(this.destroy$))
      .subscribe((terms) => {
        if (terms) {
          this._verifyTerms(terms);
        }
      });

    this._termsService.termsDataError$
      .pipe(takeUntil(this.destroy$))
      .subscribe((flag) => {
        this.hasError = flag;
      });
  }

  private _handleLinkError(): void {
    this.showRequiredTerm = false;
    this.showAlertError = true;
    this.alertErrorTitle = 'Não foi possível exibir o arquivo do termo.';
    this.alertErrorMessage =
      'Por favor, tente novamente mais tarde e, se o erro persistir, entre em contato com a nossa central de atendimento.';
  }

  setRememberLater(): void {
    localStorage.setItem('isRememberLater', 'true');
  }

  buildDataLayer() {
    const pInfo = new PageInfo();
    pInfo.url = location.href;
    pInfo.pageName = 'MP:LG:IN:MenuDeProdutos:Dash';
    pInfo.siteSection = 'MenuDeProdutos';
    pInfo.ambiente = 'LG';

    document.DataLayer = new DataLayer();
    document.DataLayer.pageInfo = pInfo;
    document.DataLayer.rule = 'pageLoad';
  }

  dispatchEvent(eventName: string) {
    const satellite = window['_satellite'];

    if (satellite) {
      document.dispatchEvent(new CustomEvent(eventName));
    } else {
      setTimeout(() => this.dispatchEvent(eventName), 500);
    }
  }

  getErrorDetail = (error: HttpErrorResponse, method: string) => {
    let detail = '';
    let message: string;

    if (error) {
      // add back-end error content if exists
      if (error.error && error.error.length > 0 && !!error.error[0].code) {
        detail = `CODE:${error.error[0].code}:`;
        message = `HTTP:${error.error[0].message}`;
      } else if (error.error && error.error.Errors) {
        detail = `CODE:${error.error.StatusCode}:`;
        message = `HTTP:${error.error.Errors[0]}`;
      } else if (error.url) {
        const url = error.url.replace(this._environmentService.env.source, '');
        const endpoint = url.split('?')[0];
        detail = `${detail}HTTP:${
          error.status || 'options'
        }:${method}/${endpoint}`;
      }

      this.errorDetailUrl(error, detail, method);

      if (message) {
        detail = `${detail}:${message}`;
      }
    }
    return detail;
  };

  errorDetailUrl(error: HttpErrorResponse, detail: any, method: string) {
    if (error.url || error['request']?.url) {
      if (error.url) {
        const url = error.url.replace(this._environmentService.env.source, '');
        const endpoint = url.split('?')[0];
        detail = `${detail}${method}/${endpoint}`; //NOSONAR
      } else {
        const url = error['request'].url.replace(
          this._environmentService.env.source,
          ''
        );
        const endpoint = url.split('?')[0];
        detail = `${detail}${method}/${endpoint}`; //NOSONAR
      }
    }
  }
}
